import React from 'react';

export interface TagProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  removable?: boolean;
  onRemove?: () => void;
}

export function Tag({ 
  children, 
  className = '', 
  variant = 'default',
  size = 'sm',
  removable = false,
  onRemove
}: TagProps) {
  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'rounded-full';
      case 'secondary':
        return 'bg-gray-100 text-gray-800 rounded-md border border-gray-200';
      case 'success':
        return 'bg-green-100 text-green-800 rounded-full';
      case 'warning':
        return 'bg-amber-50 text-amber-700 rounded-full';
      case 'error':
        return 'bg-red-100 text-red-800 rounded-full';
      default:
        return 'text-gray-800 rounded-full';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'xs':
        return 'px-2 py-0.5 text-xs';
      case 'xxs':
        return 'px-1 py-[0.05rem] text-[0.60rem]';
      case 'sm':
        return 'px-2 py-1 text-xs';
      case 'md':
        return 'px-3 py-1.5 text-sm';
      case 'lg':
        return 'px-4 py-2 text-base';
      default:
        return 'px-2 py-1 text-xs';
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--superliteprimary)',
          color: 'var(--primary)'
        };
      default:
        return {};
    }
  };

  return (
    <span 
      className={`inline-flex items-center gap-1 ${getVariantClasses()} ${getSizeClasses()} ${className}`}
      style={getVariantStyles()}
    >
      {children}
      {removable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="hover:bg-gray-200 rounded-full p-0.5 transition-colors"
          aria-label={`Remove ${children}`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
}
